import type { ReactElement } from 'react';

import { getItineraryTokenOrIdForFlightSearchResults } from '@web-platform/shared-universal-redux/src/helpers/getItineraryTokenOrIdForFlightSearchResults';
import { translate } from '@web-platform/shared-universal-translations';
import { useAdLinkVisibility } from '@web-platform/shared-universal-utils-universal/src/adverts/useAdLinkVisibility';
import { useBadgeMultiline } from '@web-platform/shared-universal-utils-universal/src/adverts/useBadgeMultiline';
import classNames from 'classnames';

import { SponsoredBadge } from '../SponsoredInfoComponents/SponsoredBadge/SponsoredBadge';

import { TicketBrand } from './TicketBrand/TicketBrand';

import type { FlightsInlineAdContainerProps } from './commonFlightsInlineAdTypes';

import STYLES from './FlightsInlineAdContainer.module.scss';

export const FlightsInlineAdContainer = ({
  AcornTicketBody,
  AcornTicketStub,
  AirlineName,
  BananaTicketBody,
  BananaTicketStub,
  FlightsTicketMobileContainer,
  Ticket,
  TicketStubContent,
  additionalText,
  additionalTitle,
  alternativePrice,
  ariaAdScenic,
  carrierAltId,
  carrierId,
  creative,
  ctaLabel,
  dataTestId,
  flightsInlineAd,
  hideDeals,
  isSponsoredSelfTransfer,
  itinerary,
  itineraryResultPosition,
  loading,
  onClick,
  partnerName,
  passengers,
  pioAgencyName,
  pioPartnerName,
  priceLabel,
  pricingOption,
  redirectUrl,
  showAirlineName,
  trackingRef,
}: FlightsInlineAdContainerProps): ReactElement => {
  const pricingOptions = pricingOption ? [pricingOption] : undefined;
  const { badgeRef, isBadgeMultiline } = useBadgeMultiline();
  const { isLinkVisible, ref: linkRef } = useAdLinkVisibility<HTMLElement>(trackingRef);
  const isAlterativePriceAvailable = !!alternativePrice;

  return (
    <div className={STYLES.container}>
      <a
        className={STYLES.link}
        ref={linkRef}
        href={redirectUrl}
        onClick={onClick}
        data-testid={dataTestId}
        target="_blank"
        rel="noreferrer"
        aria-label={flightsInlineAd ? undefined : ariaAdScenic}
      >
        <FlightsTicketMobileContainer>
          {(breakpointActive: boolean) => (
            <Ticket
              vertical={breakpointActive}
              stub={
                <div
                  className={classNames({
                    [STYLES.ticketStubVertical]: breakpointActive,
                    [STYLES.ticketStubHorizontal]: !breakpointActive,
                  })}
                >
                  {isAlterativePriceAvailable && TicketStubContent && AirlineName && (
                    <TicketStubContent
                      vertical={breakpointActive}
                      ctaLabel={ctaLabel || null}
                      price={alternativePrice}
                      loading={false}
                      passengers={passengers}
                      sponsored
                      topSlot={<AirlineName carrierName={partnerName} />}
                      sponsoredBadgeMultiline={isBadgeMultiline}
                    />
                  )}
                  {AcornTicketStub && !isAlterativePriceAvailable && (
                    <AcornTicketStub
                      itineraryId={
                        itinerary ? getItineraryTokenOrIdForFlightSearchResults(itinerary) : null
                      }
                      loading={loading}
                      sponsored
                      showSponsoredBadge
                      priceLabel={priceLabel}
                      showAirlineName={showAirlineName}
                      isSponsoredSelfTransfer={isSponsoredSelfTransfer}
                      vertical={breakpointActive}
                      pricingOptions={pricingOptions}
                      ctaLabel={ctaLabel}
                      sponsoredBadgeMultiline={isBadgeMultiline}
                    />
                  )}
                  {BananaTicketStub && !isAlterativePriceAvailable && (
                    <BananaTicketStub
                      itineraryId={
                        itinerary ? getItineraryTokenOrIdForFlightSearchResults(itinerary) : null
                      }
                      loading={loading}
                      sponsored
                      showSponsoredBadge
                      priceLabel={priceLabel}
                      showAirlineName={showAirlineName}
                      isSponsoredSelfTransfer={isSponsoredSelfTransfer}
                      vertical={breakpointActive}
                      pricingOptions={pricingOptions}
                      cta={ctaLabel}
                      hideDeals={hideDeals}
                      sponsoredBadgeMultiline={isBadgeMultiline}
                    />
                  )}
                </div>
              }
            >
              {flightsInlineAd && itinerary && pricingOption && AcornTicketBody && (
                <AcornTicketBody
                  itineraryId={getItineraryTokenOrIdForFlightSearchResults(itinerary)}
                  vertical={breakpointActive}
                  showSponsoredBadge
                  sponsoredPrice={pricingOption.price?.amount}
                  sponsoredAriaLabel={ariaAdScenic}
                  itineraryResultPosition={itineraryResultPosition}
                  sponsoredCarrier={{ name: [partnerName], id: carrierId, altId: carrierAltId }}
                  sponsored
                  sponsoredBadgeMultiline={isBadgeMultiline}
                />
              )}
              {flightsInlineAd && itinerary && pricingOption && BananaTicketBody && (
                <BananaTicketBody
                  itineraryId={getItineraryTokenOrIdForFlightSearchResults(itinerary)}
                  vertical={breakpointActive}
                  sponsored
                  showSponsoredBadge
                  sponsoredPrice={pricingOption.price?.amount}
                  sponsoredAriaLabel={ariaAdScenic}
                  loading={loading}
                  itineraryResultPosition={itineraryResultPosition}
                  sponsoredCarrier={{ name: [partnerName], id: carrierId, altId: carrierAltId }}
                  sponsoredBadgeMultiline={isBadgeMultiline}
                />
              )}
              {!flightsInlineAd && creative && (
                <div className={breakpointActive ? undefined : STYLES.ticketBrandHorizontal}>
                  <TicketBrand
                    creative={creative}
                    vertical={breakpointActive}
                    sponsoredBadgeMultiline={isBadgeMultiline}
                  />
                </div>
              )}
            </Ticket>
          )}
        </FlightsTicketMobileContainer>
      </a>
      {isLinkVisible && (
        <div
          className={classNames(STYLES.sponsoredBadge, {
            [STYLES.flightsInlineSponsoredBadge]: flightsInlineAd,
          })}
          ref={badgeRef}
        >
          <SponsoredBadge
            additionalTitle={additionalTitle}
            additionalText={additionalText}
            pioPartnerName={pioPartnerName}
            pioAgencyName={pioAgencyName}
            ariaLabel={translate('a11y_Ad_info_learn_more')}
            withInfoIcon
          />
        </div>
      )}
    </div>
  );
};
